import { authHeaders } from '@/utils/authToken';
import type { DriverDocument, DocumentType } from '@/types';

export const documentService = {
  async list(): Promise<DriverDocument[]> {
    const res = await fetch('/api/documents', { headers: await authHeaders() });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error);
    return json.data;
  },

  async upload(doc: {
    document_type_id: string;
    file_url: string;
    file_name: string;
    file_size: number;
    mime_type: string;
    vehicle_id?: string;
    issue_date?: string;
    expiration_date?: string;
  }): Promise<DriverDocument> {
    const res = await fetch('/api/documents', {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify(doc),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error);
    return json.data;
  },

  async validate(id: string): Promise<DriverDocument> {
    const res = await fetch(`/api/documents/${id}/validate`, {
      method: 'POST',
      headers: await authHeaders(),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error);
    return json.data;
  },

  async getDocumentTypes(): Promise<DocumentType[]> {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    const response = await fetch(`${supabaseUrl}/rest/v1/document_types?is_active=eq.true&order=name`, {
      headers: {
        'apikey': supabaseKey,
        'Authorization': `Bearer ${supabaseKey}`,
        'Accept': 'application/json',
        'Accept-Profile': 'portal',
      },
    });
    if (!response.ok) throw new Error('Failed to load document types');
    return response.json();
  },

  async uploadFile(file: File, path: string): Promise<string> {
    const arrayBuffer = await file.arrayBuffer();
    const base64 = btoa(
      new Uint8Array(arrayBuffer).reduce((s, b) => s + String.fromCharCode(b), '')
    );

    const res = await fetch('/api/documents/upload', {
      method: 'POST',
      headers: { ...authHeaders() },
      body: JSON.stringify({
        fileName: file.name,
        fileData: base64,
        contentType: file.type,
        path,
      }),
    });

    const json = await res.json();
    if (!res.ok) throw new Error(json.message || 'Upload failed');
    return json.data.publicUrl;
  },
};
